/**
 * Poker web3 connection SSOT — single map for pages.
 *
 * Player UX (keep this simple — do not add vendors):
 * 1. Grudge ID — who you are (JWT)
 * 2. Phantom Connect — how you sign (extension / Google / Apple)
 * 3. Play ledger — how the table runs (edge D1 server wallet)
 *
 * Crossmint stays **server-side** on Railway (cNFT / ops). Not a player connect CTA.
 *
 * @see docs/CROSSMINT_POKER.md · docs/WALLET_GBUX.md · Crossmint production console
 */
import { FLEET, GBUX } from "@shared/fleet";

export const WEB3 = {
  chain: "solana" as const,
  network: "mainnet-beta" as const,
  rpcDefault: "https://api.mainnet-beta.solana.com",
  mint: GBUX.mint,
  usdtMint: GBUX.usdtMint,
  decimals: GBUX.decimals,
  rateUsd: GBUX.rateUsd,
  /** Studio bag dashboard — not the poker signer */
  crossmintWalletSite: FLEET.walletSite,
  /** Who you are */
  grudgeId: FLEET.grudgeId,
  /** Sales / deposit agent (public address only) */
  budbai: "6P7Pp5eHzPAVjnbNLkW8DzAuuc7gj9Sm5XiprwnjzvRs",
  agentEmail: "poker-ai-agent@grudge-studio.com",
  /**
   * Phantom embedded — Solana Foundation template integrated into poker SPA
   * (not a second dApp). @see docs/PHANTOM_EMBEDDED_SSOT.md
   */
  phantom: {
    package: "@phantom/browser-sdk",
    /** https://solana.com/developers/templates/phantom-embedded-js */
    templateUrl: "https://solana.com/developers/templates/phantom-embedded-js",
    template:
      "solana-foundation/templates/community/phantom-embedded-js",
    templateCmd:
      "npx create-solana-dapp@latest <app> --template phantom-embedded-js",
    code: "client/src/lib/phantom-browser.ts",
    multiWalletRegistry: "client/src/lib/wallet-registry.ts",
    redirect: "https://poker.grudge-studio.com/connect",
    scopesApi: "/api/wallet/scopes",
    scopesBatchApi: "/api/wallet/scopes-batch",
    providers: ["injected", "google", "apple"] as const,
    appId: "8de264e8-1323-4b99-afec-fc13f3e060c7",
  },
  /**
   * Puter media shell — same production SPA + D1 play ledger.
   * @see docs/PUTER_GRUDGE_POKER.md
   */
  puter: {
    appName: "grudge-poker",
    appId: "app-80b4bbba-febe-47a4-b22f-344b5b19be02",
    appUrl: "https://puter.com/app/grudge-poker",
    indexUrl: "https://poker.grudge-studio.com/",
    embedHelper: "client/src/lib/puter-embed.ts",
    playLedger: "edge D1 only — never Puter KV",
  },
  /** Crossmint package map — one stack (see docs/CROSSMINT_POKER.md) */
  packages: {
    walletsSdk: "@crossmint/wallets-sdk",
    clientReact: "@crossmint/client-sdk-react-ui",
    serverModule: "server/services/crossmint-wallets.ts",
    unifiedApi: "/api/wallet/scopes",
    multiWalletApi: "/api/wallet/scopes-batch",
  },
  explorerTx: (sig: string) =>
    `https://solscan.io/tx/${sig}`,
  explorerAddress: (addr: string) =>
    `https://solscan.io/account/${addr}`,
  explorerToken: (mint: string) =>
    `https://solscan.io/token/${mint}`,
} as const;

export function isMainnetNetwork(n: string | undefined | null): boolean {
  if (!n) return true;
  const s = n.toLowerCase();
  return s === "mainnet-beta" || s === "mainnet";
}

export function assertNoCrossmintSecretInClient(): void {
  // Guardrail for agents — client must never embed server keys
  const forbidden = [
    "CROSSMINT_SERVER",
    "CROSSMINT_SECRET",
    "sk_production",
    "sk_staging",
  ];
  void forbidden;
}
